import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { useAuth } from '@/auth/AuthProvider'
import { ALLOW_SIGNUP, AUTH_DISABLED, missingAuthConfig, supabase } from '@/auth/supabase'
import { Button, Field, Input } from '@/components/ui/controls'
import { Spinner } from '@/components/ui/feedback'

/** `redirect` carries the URL the user was trying to reach, so an expired
 *  session returns them to where they were rather than dumping them on a
 *  landing page. */
const search = z.object({ redirect: z.string().optional().catch(undefined) })

export const Route = createFileRoute('/login')({
  validateSearch: search,
  component: LoginPage,
})

function LoginPage() {
  const { signIn, session, loading } = useAuth()
  const { redirect } = Route.useSearch()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!loading && session) navigate({ to: redirect ?? '/matches', replace: true })
  }, [session, loading, redirect, navigate])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(email, password)
      navigate({ to: redirect ?? '/matches', replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in')
    } finally {
      setBusy(false)
    }
  }

  if (!supabase && !AUTH_DISABLED) {
    return (
      <AuthShell title="Sign in unavailable">
        <div className="space-y-3 text-sm text-muted leading-relaxed">
          <p>
            There is no sign-in form because this build has no API key, so it cannot reach
            the authentication service.
          </p>
          <p>
            Missing from <code className="text-fg">web/.env</code>:
          </p>
          <ul className="space-y-1">
            {missingAuthConfig.map((k) => (
              <li key={k}>
                <code className="text-warn">{k}</code>
              </li>
            ))}
          </ul>
          <p className="border-t pt-3">
            Find it in the Supabase dashboard under{' '}
            <span className="text-fg">Project Settings → API Keys</span> (the{' '}
            <span className="text-fg">anon / public</span> key), paste it in, and restart
            the dev server.
          </p>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Sign in"
      footer={
        ALLOW_SIGNUP ? (
          <>
            No account?{' '}
            <Link to="/signup" className="text-accent hover:underline">
              Create one
            </Link>
          </>
        ) : (
          'Access is by invitation. Contact the administrator for an account.'
        )
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" className="w-full" disabled={busy}>
          {busy && <Spinner />}
          Sign in
        </Button>
      </form>
    </AuthShell>
  )
}

export function AuthShell({
  title,
  children,
  footer,
}: {
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <div className="grid min-h-dvh place-items-center bg-app px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="text-lg font-semibold tracking-tight text-fg">TTDB</div>
        </div>
        <div className="rounded-lg border bg-elevated p-6">
          <h1 className="mb-5 text-base font-semibold text-fg">{title}</h1>
          {children}
        </div>
        {footer && <p className="mt-4 text-center text-sm text-muted">{footer}</p>}
      </div>
    </div>
  )
}
